import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../user/user.service';
import { first, tap } from 'rxjs/operators';
import {
	AccountClient,
	AccountTierEnum,
	MultiTenantInviteDto,
} from './api.service';
import { NotificationService } from './notification/notification.service';
import { Router } from '@angular/router';
import { MultiTenantStore } from 'app/store/multi-tenant.store';
import { TaskNotificationService } from './task-notification.service';

@Injectable({
	providedIn: 'root',
})
export class MultiTenantService {
	private readonly _userService = inject(UserService);
	private readonly _notificationService = inject(NotificationService);
	private readonly _router = inject(Router);
	private readonly _accountClient = inject(AccountClient);
	private readonly _multiTenantStore = inject(MultiTenantStore);
	private readonly _taskNotifications = inject(TaskNotificationService);
	private readonly _destroyRef = inject(DestroyRef);

	private initialized = false;

	isEnterprise = computed(
		() =>
			this._userService.userSig$()?.accountTier ===
			AccountTierEnum.Enterprise
	);

	init(): void {
		// The route resolver can run more than once; a second subscription
		// would show every invite twice.
		if (this.initialized) {
			return;
		}
		this.initialized = true;

		// Invites arrive on the shared notification hub connection, which the
		// server addresses to the invited user only.
		this._taskNotifications.collaboratorInvites$
			.pipe(takeUntilDestroyed(this._destroyRef))
			.subscribe((invite) => this.showNotification(invite));
	}

	private showNotification(invite: MultiTenantInviteDto) {
		const inviteId = invite.id;
		const customerId = invite.customerId;
		if (!inviteId || customerId == null) {
			return;
		}

		let title = 'Invite to collaborate';
		let actions = [
			{
				text: 'Accept',
				command: () => this.onAcceptInvite(inviteId),
			},
			{
				text: 'Reject',
				command: () =>
					this.onRejectInvite(inviteId, customerId),
			},
		];

		let snackbarRef = this._notificationService.open({
			title,
			subtitle: `You have been invited to join ${invite.companyName}`,
			type: 'info',
			actions: actions,
		});
	}

	onAcceptInvite(inviteId: string): void {
		if (!this.isEnterprise()) {
			this._notificationService.open({
				title: 'Invite',
				subtitle:
					'Accepting External Invites requires an Enterprise Tier Upgrade',
				type: 'error',
				actions: [
					{
						text: 'Upgrade',
						command: () => {
							this._router.navigate([
								'/settings',
								'plan-billing',
							]);
						},
						variant: 'primary',
					},
				],
			});

			return;
		}

		this._accountClient
			.acceptAccountTenantInvite(inviteId)
			.pipe(
				first(),
				tap(() => this._multiTenantStore.refreshCompanies())
			)
			.subscribe((invites) => {
				this._notificationService.open({
					title: 'Invite',
					subtitle: 'Invite has been accepted!',
					type: 'success',
				});
			});
	}

	onRejectInvite(inviteId: string, customerId: number): void {
		this._accountClient
			.rejectAccountTenantInvite(inviteId, customerId)
			.pipe(first())
			.subscribe({
				next: (invites) => {
					this._notificationService.open({
						title: 'Invite',
						subtitle: 'Invite has been rejected!',
						type: 'success',
					});
				},
			});
	}
}
